import React, { useEffect, useMemo, useState } from "react";
import { access as accessApi, users as usersApi } from "@/api";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Eye,
  FolderOpen,
  File,
  PencilSimple,
  Plus,
  X,
} from "@phosphor-icons/react";

const LEVELS = {
  none: {
    label: "Aucun accès",
    description: "Aucun accès au dossier",
    icon: X,
  },
  read: {
    label: "Consultation",
    description: "Voir et télécharger",
    icon: Eye,
  },
  contribute: {
    label: "Ajout de contenu",
    description: "Voir, télécharger et ajouter",
    icon: Plus,
  },
  edit: {
    label: "Gestion complète",
    description: "Ajouter, renommer, déplacer et supprimer",
    icon: PencilSimple,
  },
};

export default function AccessModal({
  items,
  type,
  onClose,
  onSaved,
}) {
  const { user: currentUser } = useAuth();
  const first = items[0];
  const multiple = items.length > 1;
  const fixedIds = new Set(multiple ? [] : [first.ownerId || first.createdBy]);
  fixedIds.add(currentUser?.id);
  const [users, setUsers] = useState([]);
  const [levels, setLevels] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    usersApi.list()
      .then((items) => {
        const loadedUsers = items || [];
        const initial = {};

        (first.userAccessLevels || []).forEach((entry) => {
          if (LEVELS[entry.level]) {
            initial[entry.userId] = entry.level;
          }
        });

        (first.visibleToUserIds || []).forEach((userId) => {
          if (!initial[userId]) initial[userId] = "read";
        });

        (first.visibleToRoles || []).forEach((role) => {
          loadedUsers
            .filter((user) => user.role === role)
            .forEach((user) => {
              if (!initial[user.id]) initial[user.id] = "read";
            });
        });

        setUsers(loadedUsers);
        setLevels(initial);
      })
      .catch((error) => {
        setLoadError(true);
        toast.error(
          formatApiError(error.response?.data?.detail)
          || error.message
        );
      })
      .finally(() => setLoading(false));
  }, [first]);

  const accessCount = useMemo(
    () => Object.values(levels).filter((level) => level !== "none").length,
    [levels]
  );

  const setUserLevel = (userId, level) => {
    setLevels((current) => ({
      ...current,
      [userId]: level,
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const userAccessLevels = Object.entries(levels)
        .filter(([userId, level]) =>
          level && level !== "none" && !fixedIds.has(userId)
          && users.find((item) => item.id === userId)?.role !== "admin")
        .map(([userId, level]) => ({
          user_id: userId,
          level,
        }));
      const updated = await accessApi.updateBulk({
        items: items.map((item) => ({ type, id: item.id })),
        user_access_levels: userAccessLevels,
      });
      toast.success("Accès mis à jour");
      onSaved?.(updated);
      onClose();
    } catch (error) {
      toast.error(
        formatApiError(error.response?.data?.detail)
        || error.message
      );
    } finally {
      setSaving(false);
    }
  };

  const ownerId = multiple ? null : first.ownerId || first.createdBy;

  return (
    <div
      className="acta-modal-overlay fixed inset-0 flex items-center justify-center p-3 sm:p-6"
      onClick={onClose}
      data-testid="document-access-modal"
    >
      <div
        className="acta-modal-surface w-full max-w-2xl flex flex-col rounded-3xl overflow-hidden"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Gérer les accès de ${multiple ? items.length + " éléments" : first.name}`}
        style={{ maxHeight: "88dvh" }}
      >
        <div
          className="px-5 py-4 flex items-center justify-between"
          style={{ borderBottom: "1px solid var(--do-border)" }}
        >
          <div>
            <div className="label-eyebrow inline-flex items-center gap-1.5">
              {type === "folder" ? <FolderOpen size={13} weight="fill" /> : <File size={13} />}
              Gérer les accès · {type === "folder" ? "Dossier" : "Document"}
            </div>
            <div
              className="font-heading font-semibold text-sm mt-1"
              style={{ color: "var(--do-ink)" }}
            >
              {multiple ? `${items.length} éléments sélectionnés` : first.name}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-cobalt-500/10"
            aria-label="Fermer"
          >
            <X size={16} />
          </button>
        </div>

        <div
          className="px-5 py-3 text-xs"
          style={{
            color: "var(--do-muted)",
            borderBottom: "1px solid var(--do-border)",
          }}
        >
          Choisissez un niveau pour chaque utilisateur.
          {type === "folder"
            ? " Les droits sont hérités par les sous-dossiers et leur contenu."
            : " Les droits du dossier parent restent également applicables."}
          {multiple && " Ces réglages remplaceront les accès propres de chaque élément sélectionné."}
          <strong
            className="ml-2"
            style={{ color: "var(--do-ink)" }}
          >
            {accessCount} accès accordé{accessCount > 1 ? "s" : ""}
          </strong>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-6 label-eyebrow lirah-pulse">
              Chargement…
            </div>
          ) : loadError ? (
            <div className="p-6 text-sm" role="alert">
              Impossible de charger les utilisateurs. Fermez puis réessayez.
            </div>
          ) : (
            <ul>
              {users.map((user) => {
                const fixedLevel =
                  user.role === "admin" || user.id === ownerId
                  || user.id === currentUser?.id;
                const level = fixedLevel
                  ? "edit"
                  : levels[user.id] || "none";
                const meta = LEVELS[level];
                const Icon = meta.icon;

                return (
                  <li
                    key={user.id}
                    className="px-5 py-3 flex items-center gap-3"
                    style={{ borderBottom: "1px solid var(--do-border)" }}
                  >
                    <div
                      className="h-9 w-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                      style={{
                        background: "linear-gradient(135deg, var(--do-cyan), var(--do-blue-deep))",
                        color: "#050A18",
                      }}
                    >
                      {(user.firstName?.[0] || "?")
                        + (user.lastName?.[0] || "")}
                    </div>

                    <div className="min-w-0 flex-1">
                      <div
                        className="font-heading font-semibold text-sm truncate"
                        style={{ color: "var(--do-ink)" }}
                      >
                        {user.firstName} {user.lastName}
                      </div>
                      <div
                        className="text-[11px] truncate"
                        style={{ color: "var(--do-muted)" }}
                      >
                        {user.email}
                      </div>
                    </div>

                    {fixedLevel ? (
                      <div className="text-right shrink-0">
                        <div
                          className="text-xs font-semibold"
                          style={{ color: "var(--do-ink)" }}
                        >
                          {fixedIds.has(user.id) && user.role !== "admin"
                            ? "Propriétaire"
                            : "Administrateur"}
                        </div>
                        <div
                          className="text-[10px]"
                          style={{ color: "var(--do-muted)" }}
                        >
                          Gestion complète
                        </div>
                      </div>
                    ) : (
                      <label className="min-w-[210px] shrink-0">
                        <span className="sr-only">
                          Niveau d’accès de {user.firstName} {user.lastName}
                        </span>
                        <div className="relative">
                          <Icon
                            size={14}
                            className="absolute left-3 top-2.5 pointer-events-none"
                            style={{ color: "var(--do-muted)" }}
                          />
                          <select
                            value={level}
                            onChange={(event) =>
                              setUserLevel(user.id, event.target.value)
                            }
                            className="w-full rounded-lg py-2 pl-9 pr-3 text-xs"
                            style={{
                              color: "var(--do-ink)",
                              background: "var(--do-bg-solid)",
                              border: "1px solid var(--do-border-strong)",
                            }}
                            data-testid={`folder-access-${user.id}`}
                          >
                            {Object.entries(LEVELS)
                              .filter(([value]) => type === "folder" || value !== "contribute")
                              .map(([value, option]) => (
                              <option key={value} value={value}>
                                {option.label} — {option.description}
                              </option>
                            ))}
                          </select>
                        </div>
                        <span
                          className="block mt-1 text-[10px]"
                          style={{ color: "var(--do-muted)" }}
                        >
                          {meta.description}
                        </span>
                      </label>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div
          className="px-5 py-3 flex justify-end gap-2"
          style={{ borderTop: "1px solid var(--do-border)" }}
        >
          <button
            type="button"
            onClick={onClose}
            className="au-btn-ghost px-4 py-2 text-sm"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || loading || loadError}
            className="au-btn-primary px-4 py-2 text-sm"
            data-testid="folder-access-save"
          >
            {saving ? "Enregistrement…" : "Enregistrer les accès"}
          </button>
        </div>
      </div>
    </div>
  );
}
